// StudentAttendance.jsx
// Brand Kit: Soft Lavender BG #F3F3F9 | Primary Blue #6A88CE | Peach #E4916D
// Stack: React + React-Bootstrap + Custom CSS

import React, { useState, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '../../../assests/css/studentdashboard/StudentAttendance.css';
import StudentsideLayout from '../../../layouts/StudentsideLayout';

// ─────────────────────────────────────────────
//  STUDENT MOCK DATA
// ─────────────────────────────────────────────

const STUDENT = {
  name: 'Zainab Noor',
  initials: 'ZN',
  rollNo: 'BSCS-2021-F-042',
  section: 'BSCS-5A',
  semester: 'Semester 5 — Fall 2024',
  department: 'BS Computer Science',
};

const SUBJECTS = [
  {
    id: 1,
    name: 'Web Engineering',
    teacher: 'Ms. Ayesha Malik',
    totalClasses: 38,
    attended: 34,
    absent: 4,
    leave: 0,
  },
  {
    id: 2,
    name: 'Software Engineering',
    teacher: 'Dr. Kamran Zafar',
    totalClasses: 35,
    attended: 25,
    absent: 8,
    leave: 2,
  },
  {
    id: 3,
    name: 'Database Systems',
    teacher: 'Mr. Tariq Mehmood',
    totalClasses: 40,
    attended: 36,
    absent: 3,
    leave: 1,
  },
  {
    id: 4,
    name: 'Computer Networks',
    teacher: 'Ms. Sadia Hussain',
    totalClasses: 36,
    attended: 20,
    absent: 14,
    leave: 2,
  },
  {
    id: 5,
    name: 'Theory of Automata',
    teacher: 'Dr. Faisal Raza',
    totalClasses: 34,
    attended: 32,
    absent: 2,
    leave: 0,
  },
  {
    id: 6,
    name: 'Technical Writing',
    teacher: 'Ms. Kiran Shafiq',
    totalClasses: 28,
    attended: 26,
    absent: 2,
    leave: 0,
  },
];

// Calendar mock data — May 2025 (1=Mon…7=Sun, 0=empty padding)
// values: 'P' present | 'A' absent | 'L' leave | 'N' no class | 'F' future | '' empty pad
const MAY_2025 = {
  month: 'May 2025',
  startPad: 3, // May 1 = Thursday (0-indexed from Mon → pad=3)
  days: [
    // 1–4
    'P','P','N','N',
    // 5–11
    'P','A','P','P','N','N','P',
    // 12–18
    'P','P','A','P','N','N','L',
    // 19–25
    'P','P','P','A','N','N','P',
    // 26–31
    'P','P','N','N','P','P',
  ],
  totalDays: 31,
};

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────

const getPct = (attended, total) =>
  total === 0 ? 0 : Math.round((attended / total) * 100);

const getPctClass = (pct) => {
  if (pct >= 75) return 'pct-green';
  if (pct >= 60) return 'pct-amber';
  return 'pct-red';
};

const getStatusChip = (pct) => {
  if (pct >= 75) return { label: '✓ Safe',    cls: 'chip-safe' };
  if (pct >= 60) return { label: '⚠ Warning', cls: 'chip-warning' };
  return                  { label: '✗ At Risk', cls: 'chip-danger' };
};

const getOverallStatus = (pct) => {
  if (pct >= 75) return { label: 'Good Standing', cls: 'status-safe' };
  if (pct >= 60) return { label: 'Warning Zone',  cls: 'status-warning' };
  return                  { label: 'At Risk',       cls: 'status-danger' };
};

const getAlertBanner = (pct) => {
  if (pct >= 75)
    return { type: 'safe',    icon: '🎉', msg: 'Great job! Your overall attendance is above 75%. Keep it up!' };
  if (pct >= 60)
    return { type: 'warning', icon: '⚠️', msg: 'Your attendance is in the warning zone. Please attend more classes to avoid a shortfall.' };
  return   { type: 'danger',  icon: '🚨', msg: 'Critical! Your overall attendance is below 60%. You are at risk of being debarred.' };
};

// ─────────────────────────────────────────────
//  SUB-COMPONENTS
// ─────────────────────────────────────────────

/** Topbar */
const TopNavbar = () => (
  <nav className="top-navbar">
    <div className="navbar-brand">
      <div className="brand-icon">🎓</div>
      <span className="brand-name">Attend<span>IQ</span></span>
    </div>
    <div className="navbar-student-info">
      <div className="student-name-nav">
        <h6>{STUDENT.name}</h6>
        <small>{STUDENT.rollNo}</small>
      </div>
      <div className="student-avatar-nav" title={STUDENT.name}>
        {STUDENT.initials}
      </div>
    </div>
  </nav>
);

/** Stat Card */
const StatCard = ({ icon, value, label, colorClass }) => (
  <div className="stat-card">
    <div className={`stat-icon-wrap ${colorClass}`}>{icon}</div>
    <div className="stat-info">
      <h3>{value}</h3>
      <p>{label}</p>
    </div>
  </div>
);

/** Attendance Row */
const AttendanceRow = ({ subject, index }) => {
  const pct    = getPct(subject.attended, subject.totalClasses);
  const pctCls = getPctClass(pct);
  const chip   = getStatusChip(pct);

  return (
    
    <div
      className="attendance-row"
      style={{ animationDelay: `${index * 0.04}s` }}
    >
      {/* Serial */}
      <div className="row-serial">{index + 1}</div>

      {/* Subject */}
      <div className="subject-cell">
        <div className="subject-name">{subject.name}</div>
        <div className="subject-teacher">👩‍🏫 {subject.teacher}</div>
      </div>

      {/* Total */}
      <div>
        <div className="cell-value">{subject.totalClasses}</div>
        <div className="cell-sub">Total</div>
      </div>

      {/* Attended */}
      <div>
        <div className="cell-value" style={{ color: 'var(--green)' }}>
          {subject.attended}
        </div>
        <div className="cell-sub">Present</div>
      </div>

      {/* Absent */}
      <div>
        <div className="cell-value" style={{ color: 'var(--red)' }}>
          {subject.absent}
        </div>
        <div className="cell-sub">Absent</div>
      </div>

      {/* Percentage + Status */}
      <div className="pct-cell">
        <span className={`pct-number ${pctCls}`}>{pct}%</span>
        <div className="pct-bar-wrap">
          <div
            className={`pct-bar-fill ${pctCls}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className={`status-chip ${chip.cls}`}>{chip.label}</span>
      </div>
    </div>
  );
};

/** Calendar Widget */
const CalendarWidget = () => {
  const { month, startPad, days } = MAY_2025;
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const allCells = [
    ...Array(startPad).fill(''),   // padding
    ...days,
  ];

  const dayColorMap = {
    P: 'present',
    A: 'absent',
    L: 'leave',
    N: 'no-class',
    F: 'future',
    '': 'empty',
  };

  return (
    
    <div className="calendar-section">
      <div className="calendar-header">
        <button className="cal-nav-btn">‹</button>
        <h5>📅 {month} — Attendance Log</h5>
        <button className="cal-nav-btn">›</button>
      </div>

      <div className="calendar-weekdays">
        {weekdays.map(d => (
          <div key={d} className="weekday-label">{d}</div>
        ))}
      </div>

      <div className="calendar-grid">
        {allCells.map((val, idx) => {
          const dayNum = idx - startPad + 1;
          const cls    = dayColorMap[val] || 'empty';
          return (
            <div
              key={idx}
              className={`cal-day ${cls} ${dayNum === 7 ? 'today' : ''}`}
              title={
                cls === 'empty'    ? '' :
                cls === 'present'  ? `Day ${dayNum}: Present` :
                cls === 'absent'   ? `Day ${dayNum}: Absent` :
                cls === 'leave'    ? `Day ${dayNum}: Leave` :
                cls === 'no-class' ? `Day ${dayNum}: No Class` : ''
              }
            >
              {val !== '' ? dayNum : ''}
            </div>
          );
        })}
      </div>

      <div className="calendar-legend">
        <div className="legend-item"><span className="legend-dot dot-present" />Present</div>
        <div className="legend-item"><span className="legend-dot dot-absent"  />Absent</div>
        <div className="legend-item"><span className="legend-dot dot-leave"   />Leave</div>
        <div className="legend-item"><span className="legend-dot dot-no-class"/>No Class</div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────

const StudentAttendance = () => {
  const [filter, setFilter]   = useState('All');
  const [search, setSearch]   = useState('');

  // Overall stats
  const totalClasses  = SUBJECTS.reduce((s, x) => s + x.totalClasses, 0);
  const totalAttended = SUBJECTS.reduce((s, x) => s + x.attended, 0);
  const totalAbsent   = SUBJECTS.reduce((s, x) => s + x.absent, 0);
  const totalLeave    = SUBJECTS.reduce((s, x) => s + x.leave, 0);
  const overallPct    = getPct(totalAttended, totalClasses);
  const overallStatus = getOverallStatus(overallPct);
  const alert         = getAlertBanner(overallPct);

  // Filter + Search
  const filteredSubjects = useMemo(() => {
    let list = SUBJECTS;

    if (filter === 'Safe')    list = list.filter(s => getPct(s.attended, s.totalClasses) >= 75);
    if (filter === 'Warning') list = list.filter(s => { const p = getPct(s.attended, s.totalClasses); return p >= 60 && p < 75; });
    if (filter === 'At Risk') list = list.filter(s => getPct(s.attended, s.totalClasses) < 60);

    if (search.trim()) {
      list = list.filter(s =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.teacher.toLowerCase().includes(search.toLowerCase())
      );
    }

    return list;
  }, [filter, search]);

  return (
    <StudentsideLayout>
    <div className="student-page">
      <TopNavbar />

      <div className="main-content">

        {/* ── Page Header ── */}
        <div className="page-header">
          <div className="page-header-inner">
            <div>
              <h1 className="page-title">My Attendance</h1>
              <p className="page-subtitle">
                <span className="live-dot" />
                {STUDENT.semester}
              </p>
            </div>
            <span className="semester-badge">🎓 {STUDENT.section}</span>
          </div>
        </div>

        {/* ── Alert Banner ── */}
        <div className={`alert-banner ${alert.type}`}>
          <span className="alert-icon">{alert.icon}</span>
          {alert.msg}
        </div>

        {/* ── Student Profile Card ── */}
        <div className="profile-card">
          <div className="profile-avatar-lg">{STUDENT.initials}</div>
          <div className="profile-info">
            <div className="profile-name">{STUDENT.name}</div>
            <div className="profile-meta">
              <span className="profile-meta-item">🪪 {STUDENT.rollNo}</span>
              <span className="profile-meta-item">🏛 {STUDENT.department}</span>
              <span className="profile-meta-item">👥 {STUDENT.section}</span>
            </div>
          </div>
          <div className="profile-overall">
            <div className="overall-pct">{overallPct}%</div>
            <div className="overall-label">Overall Attendance</div>
            <span className={`overall-status ${overallStatus.cls}`}>
              {overallStatus.label}
            </span>
          </div>
        </div>

        {/* ── Summary Stats ── */}
        <div className="summary-stats">
          <Row className="g-3">
            <Col xs={6} sm={6} lg={3}>
              <StatCard
                icon="📚" value={totalClasses}
                label="Total Classes" colorClass="icon-blue"
              />
            </Col>
            <Col xs={6} sm={6} lg={3}>
              <StatCard
                icon="✅" value={totalAttended}
                label="Classes Attended" colorClass="icon-green"
              />
            </Col>
            <Col xs={6} sm={6} lg={3}>
              <StatCard
                icon="❌" value={totalAbsent}
                label="Classes Absent" colorClass="icon-red"
              />
            </Col>
            <Col xs={6} sm={6} lg={3}>
              <StatCard
                icon="📋" value={totalLeave}
                label="Leave Taken" colorClass="icon-peach"
              />
            </Col>
          </Row>
        </div>

        <Row className="g-4">
          {/* ── LEFT: Subject-wise Table ── */}
          <Col xs={12} lg={7}>
            <div className="section-title">📖 Subject-wise Attendance</div>

            {/* Filter Bar */}
            <div className="filter-bar">
              <span className="filter-label">Filter:</span>
              <div className="filter-pills">
                {['All', 'Safe', 'Warning', 'At Risk'].map(f => (
                  <button
                    key={f}
                    className={`filter-pill ${filter === f ? 'active' : ''}`}
                    onClick={() => setFilter(f)}
                  >
                    {f === 'Safe'    ? '✓ Safe'    :
                     f === 'Warning' ? '⚠ Warning' :
                     f === 'At Risk' ? '✗ At Risk'  : 'All'}
                  </button>
                ))}
              </div>
              <div className="filter-divider" />
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search subject..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Table */}
            <div className="table-card">
              <div className="table-header-row">
                <div>#</div>
                <div>Subject</div>
                <div style={{ textAlign: 'center' }}>Total</div>
                <div style={{ textAlign: 'center' }}>Present</div>
                <div style={{ textAlign: 'center' }}>Absent</div>
                <div style={{ textAlign: 'center' }}>Status</div>
              </div>
              <div className="table-body">
                {filteredSubjects.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📭</div>
                    <h5>No subjects found</h5>
                    <p>Try changing the filter or search term</p>
                  </div>
                ) : (
                  filteredSubjects.map((subject, idx) => (
                    <AttendanceRow
                      key={subject.id}
                      subject={subject}
                      index={idx}
                    />
                  ))
                )}
              </div>
            </div>
          </Col>

          {/* ── RIGHT: Calendar ── */}
          <Col xs={12} lg={5}>
            <div className="section-title">🗓 Monthly Log</div>
            <CalendarWidget />

            {/* Quick info cards */}
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              {SUBJECTS.filter(s => getPct(s.attended, s.totalClasses) < 75).map(s => {
                const pct = getPct(s.attended, s.totalClasses);
                const needed = Math.ceil((0.75 * s.totalClasses - s.attended) / 0.25);
                return (
                  <div
                    key={s.id}
                    style={{
                      background: pct < 60 ? 'var(--red-light)' : 'var(--amber-light)',
                      border: `1.5px solid ${pct < 60 ? '#fca5a5' : '#fcd34d'}`,
                      borderRadius: 'var(--radius-md)',
                      padding: '0.75rem 1rem',
                      display: 'flex', alignItems: 'center', gap: 10,
                      animation: 'fadeIn 0.4s ease both',
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>{pct < 60 ? '🚨' : '⚠️'}</span>
                    <div>
                      <div style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 700, fontSize: '0.82rem',
                        color: pct < 60 ? 'var(--red)' : 'var(--amber)',
                      }}>
                        {s.name}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--warm-gray)' }}>
                        {pct}% attendance — attend <strong>{needed}</strong> more class{needed !== 1 ? 'es' : ''} to reach 75%
                      </div>
                    </div>
                  </div>
                );
              })}

              {SUBJECTS.every(s => getPct(s.attended, s.totalClasses) >= 75) && (
                <div style={{
                  background: 'var(--green-light)', border: '1.5px solid #86efac',
                  borderRadius: 'var(--radius-md)', padding: '0.85rem 1rem',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: '1rem' }}>🎉</span>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 700, fontSize: '0.82rem', color: 'var(--green)',
                  }}>
                    All subjects above 75% — you're in great shape!
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
    </StudentsideLayout>
  );
};

export default StudentAttendance;